<template>
    <div class="form-container">
      <el-form :model="form" label-width="180px" ref="formRef">
        <!-- 遍历表单配置 -->
        <template v-for="(item, index) in formItems">
          <!-- 分组（嵌套对象） -->
          <el-card v-if="item.type === 'group'" :key="'group-' + item.label" class="group-card">
            <div slot="header">
              <span>{{ item.label }}</span>
            </div>
            <el-form-item 
              v-for="(child, childIndex) in item.children" 
              :key="'child-' + child.prop" 
              :label="child.label"
              :prop="child.prop"
            >
              <!-- 根据类型渲染不同组件 -->
              <component 
                :is="getComponent(child)" 
                v-model="form[child.prop]"
                v-bind="child.component || {}"
                @input="(val) => { form[child.prop] = val }"
                @change="(val) => { form[child.prop] = val }"
              >
                <!-- 如果是选择器，渲染选项 -->
                <el-option 
                  v-if="child.type === 'select'"
                  v-for="(opt, optIndex) in child.options"
                  :key="optIndex"
                  :label="opt.label"
                  :value="opt.value"
                ></el-option>
              </component>
            </el-form-item>
          </el-card>
  
          <!-- 普通表单项 -->
          <el-form-item 
            v-else 
            :key="'item-' + item.prop" 
            :label="item.label" 
            :prop="item.prop"
          >
            <!-- 根据类型渲染不同组件 -->
            <component 
              :is="getComponent(item)" 
              v-model="form[item.prop]"
              v-bind="item.component || {}"
              @input="(val) => { form[item.prop] = val }"
              @change="(val) => { form[item.prop] = val }"
            >
              <!-- 如果是选择器，渲染选项 -->
              <el-option 
                v-if="item.type == 'select'"
                v-for="(opt, optIndex) in item.options"
                :key="optIndex"
                :label="opt.label"
                :value="opt.value"
              ></el-option>
            </component>
          </el-form-item>
        </template>
      </el-form>
  
      <div class="actions">
        <el-button type="primary" @click="submitForm">提交</el-button>
        <el-button @click="cancelForm">取消</el-button>
      </div>
    </div>
  </template>

<script>

export default {
    name: 'FormatterJson',
    props: {
        jsonData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            formConfig: [],
            form: {},
            formItems: []
        }
    },
    watch: {
        jsonData: {
            handler(newVal) {
                this.formItems = this.jsonToElementForm(newVal)
                this.initFormData()
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        // 初始化表单数据
        initFormData() {
            const newForm = {}
            this.formItems.forEach(item => {
                if (item.type === 'group') {
                    item.children.forEach(child => {
                        newForm[child.prop] = child.value
                    })
                } else {
                    newForm[item.prop] = item.value
                }
            })
            this.form = newForm
        },
        // 获取组件类型
        getComponent(item) {
            const componentMap = {
                'input': 'el-input',
                'select': 'el-select',
                'date-picker': 'el-date-picker'
            }
            return componentMap[item.type] || 'el-input'
        },
        // 提交表单
        submitForm() {
            this.$refs.formRef.validate((valid) => {
                if (valid) {
                    // 处理时间戳
                    const formData = { ...this.form };
                    if (formData.autoMarkUpdateTime) {
                        formData.autoMarkUpdateTime = new Date(formData.autoMarkUpdateTime).getTime();
                    }

                    // 转换回原始结构
                    const originalData = this.convertToOriginalStructure(formData);
                    console.log('表单数据：', originalData);
                    this.$emit('submit', JSON.stringify(originalData));
                }
            });
        },
        // 修改convertToOriginalStructure方法，处理多个intensity输入框
        convertToOriginalStructure(formData) {
            const result = {};
            const order = [];
            
            // 定义需要特殊处理的结构
            const specialStructures = ['Filter'];
            
            // 处理特殊结构
            for (const key in formData) {
                const parts = key.split('-');
                if (parts.length >= 2) {
                    const mainKey = parts[0];
                    
                    // 处理options的intensity
                    if (parts.length >= 4 && parts[1] === 'option' && parts[3] === 'intensity') {
                        const optionIndex = parseInt(parts[2]);
                        
                        // 确保主结构已初始化
                        if (!result[mainKey]) {
                            result[mainKey] = {
                                selected_option: undefined,
                                options: []
                            };
                            order.push(mainKey);
                        }
                        
                        // 初始化或获取options数组
                        const options = Array.isArray(result[mainKey].options) ? 
                            result[mainKey].options : [];
                        
                        // 从formItems中获取当前option对应的信息
                        const groupItem = this.formItems.find(item => 
                            item.type === 'group' && 
                            item.label === mainKey
                        );
                        
                        if (groupItem && groupItem.children) {
                            // 确保我们有足够的children项
                            if (groupItem.children.length > optionIndex + 1) { // +1 因为第一个是selected_option
                                const optionName = groupItem.children[0].options[optionIndex].value;
                                
                                // 查找或创建此option
                                let optionObj = options.find(o => o.option === optionName);
                                if (!optionObj) {
                                    optionObj = { option: optionName, intensity: 0 };
                                    options.push(optionObj);
                                }
                                
                                // 更新intensity值
                                optionObj.intensity = Number(formData[key]);
                            }
                        }
                        
                        result[mainKey].options = options;
                        continue;
                    }
                    
                    // 处理预定义的特殊结构（如 Filter）
                    if (specialStructures.includes(mainKey)) {
                        if (!result[mainKey]) {
                            result[mainKey] = {
                                intensity: undefined,
                                selected_option: undefined,
                                options: []
                            };
                            order.push(mainKey);
                        }
                        
                        // 获取对应的表单项配置
                        const groupItem = this.formItems.find(item => 
                            item.type === 'group' && 
                            item.label === mainKey
                        );
                        
                        if (groupItem) {
                            const selectItem = groupItem.children.find(child => 
                                child.type === 'select'
                            );
                            if (selectItem) {
                                // 从表单项配置中获取 selected_option 的值
                                const selectedOptionProp = `${mainKey}-selected-option`;
                                if (formData[selectedOptionProp] !== undefined) {
                                    result[mainKey].selected_option = formData[selectedOptionProp];
                                }
                                
                                // 处理options数组
                                if (selectItem.options && selectItem.options.length > 0) {
                                    // 保留原始的options数组结构
                                    let originalOptions = this.formItems.find(item => 
                                        item.type === 'group' && 
                                        item.label === mainKey
                                    )?.children?.find(child => 
                                        child.prop === `${mainKey}-selected-option`
                                    )?.options?.map(opt => ({
                                        option: opt.value,
                                        intensity: 0
                                    })) || [];
                                    
                                    // 尝试获取原始数据中的options
                                    if (this.jsonData && this.jsonData[mainKey] && Array.isArray(this.jsonData[mainKey].options)) {
                                        if (this.jsonData[mainKey].options.length > 0 && 
                                            typeof this.jsonData[mainKey].options[0] === 'object' && 
                                            'option' in this.jsonData[mainKey].options[0]) {
                                            originalOptions = this.jsonData[mainKey].options;
                                        }
                                    }
                                    
                                    const hasObjectOptions = originalOptions.length > 0 && 
                                        typeof originalOptions[0] === 'object' && 
                                        'option' in originalOptions[0];
                                    
                                    // 创建映射以保留原始intensity值
                                    const intensityMap = {};
                                    if (hasObjectOptions) {
                                        originalOptions.forEach(opt => {
                                            intensityMap[opt.option] = Number(opt.intensity || 0);
                                        });
                                    }
                                    
                                    result[mainKey].options = selectItem.options.map(opt => {
                                        const optionName = opt.value;
                                        // 如果是选中的选项，使用表单中的intensity值
                                        // 否则使用原始intensity值（如果有）
                                        const intensity = (optionName === result[mainKey].selected_option) ?
                                            Number(formData[`${mainKey}-intensity`] || 0) : 
                                            (intensityMap[optionName] !== undefined ? intensityMap[optionName] : 0);
                                        
                                        return {
                                            option: optionName,
                                            intensity: intensity
                                        };
                                    });
                                }
                            }
                        }
                        
                        if (parts[parts.length - 1] === 'intensity') {
                            result[mainKey].intensity = Number(formData[key]);
                        }
                        continue;
                    }
                    
                    // 处理其他包含 selected_option 的结构
                    if (parts[parts.length - 1] === 'selected-option') {
                        if (!result[mainKey]) {
                            result[mainKey] = {
                                intensity: 0,
                                selected_option: undefined,
                                options: []
                            };
                            order.push(mainKey);
                        }
                        
                        // 设置selected_option
                        result[mainKey].selected_option = formData[key];
                        
                        // 设置默认options结构
                        if (!Array.isArray(result[mainKey].options) || result[mainKey].options.length === 0) {
                            // 获取对应的表单项配置
                            const groupItem = this.formItems.find(item => 
                                item.type === 'group' && 
                                item.label === mainKey
                            );
                            
                            if (groupItem) {
                                const selectItem = groupItem.children.find(child => 
                                    child.type === 'select'
                                );
                                if (selectItem && selectItem.options) {
                                    result[mainKey].options = selectItem.options.map(opt => ({
                                        option: opt.value,
                                        intensity: 0
                                    }));
                                }
                            }
                        }
                        
                        continue;
                    }
                    
                    // 处理intensity字段
                    if (parts[parts.length - 1] === 'intensity') {
                        const subKey = parts.slice(1, -1).join('_');
                        const intensityValue = Number(formData[key]);
                        
                        if (!result[mainKey]) {
                            result[mainKey] = {};
                            order.push(mainKey);
                        }
                        
                        // 如果mainKey存在selected_option属性，同步更新对应option的intensity
                        if (result[mainKey].selected_option && result[mainKey].options) {
                            // 更新mainKey的intensity值
                            result[mainKey].intensity = intensityValue;
                            
                            // 更新选中选项的intensity值
                            const selectedOption = result[mainKey].selected_option;
                            const optionIndex = result[mainKey].options.findIndex(opt => opt.option === selectedOption);
                            if (optionIndex >= 0) {
                                result[mainKey].options[optionIndex].intensity = intensityValue;
                            }
                        } else if (subKey) {
                            if (!result[mainKey][subKey]) {
                                result[mainKey][subKey] = {};
                            }
                            result[mainKey][subKey].intensity = intensityValue;
                        }
                        continue;
                    }
                }
            }

            // 处理其他嵌套结构
            for (const key in formData) {
                // 跳过已经处理的特殊结构字段
                if (specialStructures.some(structure => key.startsWith(structure + '-')) || 
                    key.includes('-selected_option')) {
                    continue;
                }

                const parts = key.split('-');
                if (parts.length > 1) {
                    const mainKey = parts[0];
                    if (!result[mainKey]) {
                        result[mainKey] = {};
                        order.push(mainKey);
                    }

                    // 处理 intensity 字段
                    if (parts[parts.length - 1] === 'intensity') {
                        const subKey = parts.slice(1, -1).join('_');
                        if (!result[mainKey][subKey]) {
                            result[mainKey][subKey] = {};
                        }
                        result[mainKey][subKey].intensity = Number(formData[key]);
                    } else {
                        // 处理其他嵌套字段
                        const subKey = parts.slice(1).join('_');
                        result[mainKey][subKey] = formData[key];
                    }
                } else {
                    // 处理普通字段
                    result[key] = formData[key];
                    order.push(key);
                }
            }

            // 创建有序结果对象
            const orderedResult = {};
            order.forEach(key => {
                orderedResult[key] = result[key];
            });

            return orderedResult;
        },
        // 重置表单
        resetForm() {
            this.$refs.formRef.resetFields()
        },
        // 取消表单
        cancelForm() {
            this.$refs.formRef.resetFields();
            this.$emit('cancel');
        },
        // 扁平化原始数据
        jsonToElementForm(jsonData) {
            const formItems = [];

            for (const key in jsonData) {
                if (key === 'autoMarkUpdateTime') {
                    // 时间选择器
                    formItems.push({
                        type: 'date-picker',
                        label: 'Auto Mark Update Time',
                        prop: key,
                        value: jsonData[key] ? new Date(jsonData[key]) : null,
                        component: {
                            type: 'datetime',
                            valueFormat: 'timestamp',
                            format: 'yyyy-MM-dd HH:mm:ss',
                            placeholder: '选择日期时间'
                        }
                    });
                } else if (typeof jsonData[key] === 'object' && jsonData[key] !== null) {
                    // 处理嵌套对象
                    if ('options' in jsonData[key] && 'selected_option' in jsonData[key]) {
                        // 处理包含 options 和 selected_option 的结构
                        const items = [];
                        
                        // 处理不同格式的options数组
                        if (Array.isArray(jsonData[key].options) && jsonData[key].options.length > 0) {
                            // 新格式options: [{option: "name", intensity: 0}]
                            if (typeof jsonData[key].options[0] === 'object' && 'option' in jsonData[key].options[0]) {
                                // 添加selected_option选择器
                                items.push({
                                    type: 'select',
                                    label: `${key} Selected Option`,
                                    prop: `${key}-selected-option`,
                                    value: jsonData[key].selected_option || '',
                                    options: jsonData[key].options.map(opt => ({
                                        label: opt.option,
                                        value: opt.option
                                    }))
                                });
                                
                                // 为每个option添加单独的intensity输入框
                                jsonData[key].options.forEach((opt, index) => {
                                    items.push({
                                        type: 'input',
                                        label: `${opt.option} Intensity`,
                                        prop: `${key}-option-${index}-intensity`,
                                        value: opt.intensity,
                                        component: {
                                            type: 'input',
                                            placeholder: `Enter ${opt.option} intensity`
                                        }
                                    });
                                });
                            } 
                            // 旧格式options: ["option1", "option2"]
                            else if (typeof jsonData[key].options[0] === 'string') {
                                // 添加selected_option选择器
                                items.push({
                                    type: 'select',
                                    label: `${key} Selected Option`,
                                    prop: `${key}-selected-option`,
                                    value: jsonData[key].selected_option || '',
                                    options: jsonData[key].options.map(option => ({
                                        label: option,
                                        value: option
                                    }))
                                });
                                
                                // 为每个option添加单独的intensity输入框，默认值为0
                                jsonData[key].options.forEach((option, index) => {
                                    items.push({
                                        type: 'input',
                                        label: `${option} Intensity`,
                                        prop: `${key}-option-${index}-intensity`,
                                        value: 0,
                                        component: {
                                            type: 'input',
                                            placeholder: `Enter ${option} intensity`
                                        }
                                    });
                                });
                            }
                        }
                        
                        formItems.push({
                            type: 'group',
                            label: key.replace(/_/g, '-'),
                            children: items
                        });
                    } else {
                        const nestedItems = [];
                        for (const subKey in jsonData[key]) {
                            if (typeof jsonData[key][subKey] === 'object' && jsonData[key][subKey] !== null && 'intensity' in jsonData[key][subKey]) {
                                // 处理包含 intensity 的嵌套对象
                                nestedItems.push({
                                    type: 'input',
                                    label: `${subKey.replace(/_/g, '-')} Intensity`,
                                    prop: `${key}-${subKey}-intensity`,
                                    value: jsonData[key][subKey].intensity,
                                    component: {
                                        type: 'input',
                                        placeholder: 'Enter intensity'
                                    }
                                });
                            } else {
                                // 递归处理其他嵌套对象
                                const subItems = this.jsonToElementForm(jsonData[key][subKey]);
                                nestedItems.push(...subItems);
                            }
                        }
                        formItems.push({
                            type: 'group',
                            label: key.replace(/_/g, '-'),
                            children: nestedItems
                        });
                    }
                } else {
                    // 普通输入框
                    formItems.push({
                        type: 'input',
                        label: key.replace(/_/g, '-'),
                        prop: key,
                        value: jsonData[key],
                        component: {
                            type: 'input',
                            placeholder: `请输入${key.replace(/_/g, '-')}`
                        }
                    });
                }
            }

            return formItems;
        }
    }
}
</script>

<style scoped>
.form-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
}

.group-card {
  margin-bottom: 20px;
}

.actions {
  margin-top: 20px;
  text-align: center;
}

.form-data {
  margin-top: 30px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
